import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { EstimateStratigraphy } from '@/types/estimateStratigraphy';
import { computeStratigraphyCosts } from '@/components/configurator-v2/hooks/computeStratigraphyCosts';
import type { LayerV2 } from '@/components/configurator-v2/types';
import { fetchFinishCostSnapshot } from '@/utils/estimates/fetchFinishCostSnapshot';
import { finishedFacesFor } from '@/components/configurator-v2/hooks/useTypologyDetection';
import { useOrgProfile } from '@/hooks/useOrgSettings';
import { useWasteFactors } from '@/hooks/useWasteFactors';
import { mergeEstimatePricing } from '@/utils/pricing/mergeEstimatePricing';
import { rowCostOptionsFrom, wasteAndDisposalPerSqm } from '@/utils/pricing/computeEstimateCostBase';

interface BulkUpdateParams {
  estimateStratigraphies: EstimateStratigraphy[];
  /**
   * Se true, il toast riassuntivo dell'onSuccess viene saltato. Usato dai
   * chiamanti single-row (es. useEstimateStratigraphies.updateStratigraphyPrices)
   * che mostrano un toast più specifico col delta prezzo.
   */
  silentToast?: boolean;
}

export type BulkUpdateSkipReason =
  /** Manca il riferimento alla stratigrafia originale (snapshot orfano da V1 o post-eliminazione). */
  | 'no_original_stratigraphy'
  /** La stratigrafia originale è stata eliminata dal catalogo. */
  | 'original_not_found'
  /** Composizione corrotta o vuota: ricalcolo darebbe 0, non sovrascrivo. */
  | 'zero_cost_recalc'
  /** Nessun layer valido trovato (tutti senza materiale o spessore 0). */
  | 'no_valid_layers';

export interface BulkUpdateRowOk {
  id: string;
  name: string;
  oldCost: number;
  newCost: number;
  changed: boolean; // true se la differenza > 0.01€
}

export interface BulkUpdateRowSkipped {
  id: string;
  name: string;
  reason: BulkUpdateSkipReason;
  /** Per zero_cost_recalc: dettaglio sui layer invalidi/totali. */
  detail?: string;
}

export interface BulkUpdateRowFailed {
  id: string;
  name: string;
  error: string;
}

export interface BulkUpdateReport {
  estimateId: string;
  updated: BulkUpdateRowOk[];
  skipped: BulkUpdateRowSkipped[];
  failed: BulkUpdateRowFailed[];
  costPerHour: number;
}

const SKIP_REASON_LABEL: Record<BulkUpdateSkipReason, string> = {
  no_original_stratigraphy: 'Snapshot orfano (manca riferimento al catalogo)',
  original_not_found: 'Stratigrafia originale eliminata dal catalogo',
  zero_cost_recalc: 'Ricalcolo darebbe 0€ (composizione corrotta)',
  no_valid_layers: 'Nessun layer valido nella stratigrafia',
};

export function bulkUpdateSkipReasonLabel(reason: BulkUpdateSkipReason): string {
  return SKIP_REASON_LABEL[reason];
}

/**
 * Aggiornamento bulk dei prezzi delle stratigrafie di un preventivo.
 *
 * Ricalcola dinamicamente costo materiali + viti + manodopera con il
 * `cost_per_hour` corrente (da `configurator_settings`), poi aggiorna lo
 * snapshot in `estimate_stratigraphies` (unit_cost, total_cost,
 * stratigraphy_data, layers_data, prices_updated_at).
 *
 * Ritorna un report strutturato con esiti per riga: il chiamante può
 * mostrare un dialog onesto invece della vecchia progress bar fake.
 *
 * Sicurezza: blocca se preventivo `contracted` o se l'utente non possiede
 * il progetto.
 */
export const useBulkUpdateEstimateStratigraphyPrices = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Servono a includere sfrido e smaltimento nel costo salvato della riga.
  const { data: orgProfile } = useOrgProfile();
  const { wasteMap } = useWasteFactors();

  const bulkUpdateMutation = useMutation<BulkUpdateReport, Error, BulkUpdateParams>({
    mutationFn: async ({ estimateStratigraphies }) => {
      if (!user) throw new Error('User not authenticated');
      if (estimateStratigraphies.length === 0) {
        throw new Error('Nessuna stratigrafia da aggiornare');
      }

      const estimateId = estimateStratigraphies[0].estimateId;

      const { data: estimateData, error: estimateError } = await supabase
        .from('estimates')
        .select('status, project_id, organization_id, pricing_overrides')
        .eq('id', estimateId)
        .single();
      if (estimateError) throw new Error('Preventivo non trovato');
      // F30: blocca se il preventivo è chiuso.
      if (estimateData.status === 'won' || estimateData.status === 'lost' || estimateData.status === 'contracted') {
        const label = estimateData.status === 'lost' ? 'perso' : 'vinto';
        throw new Error(`Impossibile aggiornare prezzi: preventivo ${label}`);
      }

      // Accessibilita' progetto via RLS org-scoped (is_member_of): niente filtro
      // user_id, altrimenti un membro del team non puo' aggiornare i prezzi.
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('id', estimateData.project_id)
        .single();
      if (projectError || !projectData) throw new Error('Accesso negato al progetto');

      // Costo orario corrente: letto UNA volta sola per il batch, non per riga.
      const { data: settingData, error: settingError } = await supabase
        .from('configurator_settings')
        .select('value')
        .eq('key', 'cost_per_hour')
        .maybeSingle();
      if (settingError) throw new Error('Errore nel recupero del costo orario corrente');
      const costPerHour = parseFloat(settingData?.value || '30') || 30;

      // Opzioni di costo del preventivo (org + override): valgono per tutte le
      // righe del batch, quindi si risolvono una volta sola.
      const costOpts = rowCostOptionsFrom(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mergeEstimatePricing(orgProfile ?? null, (estimateData as any)?.pricing_overrides),
        wasteMap,
      );

      // Serve a rileggere i livelli finitura dell'org (vedi blocco finitura piu`
      // sotto): la riga da sola non sa a quale azienda appartiene.
      const organizationId = (estimateData as { organization_id?: string | null }).organization_id ?? null;
      // Memo per (livello, facce). Senza, il refresh dalla dashboard rileggerebbe
      // gli stessi 4 livelli una volta per riga: su 120 righe sono ~480 query
      // inutili. La chiave include le facce perche' il costo e` gia` dimezzato
      // per contropareti e controsoffitti.
      const finishCache = new Map<string, Awaited<ReturnType<typeof fetchFinishCostSnapshot>>>();
      const finishSnapshotFor = async (code: string, faces: 1 | 2) => {
        const key = `${code}|${faces}`;
        if (!finishCache.has(key)) {
          finishCache.set(key, await fetchFinishCostSnapshot(organizationId as string, code, faces));
        }
        return finishCache.get(key) ?? null;
      };

      const updated: BulkUpdateRowOk[] = [];
      const skipped: BulkUpdateRowSkipped[] = [];
      const failed: BulkUpdateRowFailed[] = [];

      for (const estStrat of estimateStratigraphies) {
        if (!estStrat.originalStratigraphyId) {
          // Snapshot orfano: non possiamo ricalcolare ma marchiamo prices_updated_at=NOW()
          // così la riga smette di apparire nel banner "preventivi stale". L'utente vede
          // chiaramente che è skippata nel dialog di esito.
          await supabase
            .from('estimate_stratigraphies')
            .update({ prices_updated_at: new Date().toISOString() })
            .eq('id', estStrat.id);
          skipped.push({ id: estStrat.id, name: estStrat.name, reason: 'no_original_stratigraphy' });
          continue;
        }

        try {
          const { data: original, error: stratigraphyError } = await supabase
            .from('stratigraphies')
            .select(`
              *,
              layers (
                id,
                position,
                thickness,
                inter_axis,
                material_id,
                screw_material_id,
                anchor_material_id,
                screw_cost_per_sqm,
                screw_quantity,
                material_cost_per_sqm,
                labor_cost_per_sqm,
                labor_cost_per_hour,
                installation_time_minutes,
                material_quantity_per_sqm,
                materials!layers_material_id_fkey (
                  id,
                  name,
                  category,
                  unit,
                  unit_price,
                  incidence_per_sqm,
                  color_hex,
                  supplier,
                  code,
                  description,
                  width,
                  weight_per_sqm,
                  thermal_conductivity,
                  acoustic_performance,
                  fire_resistance_class,
                  box_pieces,
                  installation_time_per_sqm,
                  waste_percentage
                ),
                screw_materials:materials!layers_screw_material_id_fkey (
                  id,
                  name,
                  unit_price,
                  code,
                  supplier,
                  unit,
                  length,
                  box_pieces,
                  installation_time_per_sqm,
                  waste_percentage
                ),
                anchor_materials:materials!layers_anchor_material_id_fkey (
                  id,
                  name,
                  unit_price,
                  code,
                  supplier,
                  unit,
                  length,
                  box_pieces,
                  installation_time_per_sqm,
                  waste_percentage
                )
              )
            `)
            .eq('id', estStrat.originalStratigraphyId)
            .maybeSingle();

          if (stratigraphyError) {
            failed.push({ id: estStrat.id, name: estStrat.name, error: stratigraphyError.message });
            continue;
          }
          if (!original) {
            // Stratigrafia originale cancellata: marca come "verificata" comunque per
            // togliere dal banner stale (non si può ricalcolare senza original).
            await supabase
              .from('estimate_stratigraphies')
              .update({ prices_updated_at: new Date().toISOString() })
              .eq('id', estStrat.id);
            skipped.push({ id: estStrat.id, name: estStrat.name, reason: 'original_not_found' });
            continue;
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const dbLayers: any[] = original.layers ?? [];
          if (dbLayers.length === 0) {
            skipped.push({ id: estStrat.id, name: estStrat.name, reason: 'no_valid_layers' });
            continue;
          }

          // 🔥 F19.4 — Override unit_price col NETTO da materials_with_pricing.
          // Il join via FK carica `materials` raw dove unit_price = list_price
          // perché il trigger di recompute filtra per organization_id (i
          // materiali catalogo globale hanno org=NULL → non vengono mai
          // ricomputati). La view applica gli sconti famiglia + extra
          // correttamente per l'org corrente (RLS).
          const layerMatIds: string[] = [];
          for (const dbL of dbLayers) {
            if (dbL.materials?.id) layerMatIds.push(dbL.materials.id);
            if (dbL.screw_materials?.id) layerMatIds.push(dbL.screw_materials.id);
            if (dbL.anchor_materials?.id) layerMatIds.push(dbL.anchor_materials.id);
          }
          const uniqueLayerMatIds = Array.from(new Set(layerMatIds));
          const layerPriceMap = new Map<string, number>();
          const layerEffMap = new Map<string, { waste: number | null; disposal: number | null }>();
          if (uniqueLayerMatIds.length > 0) {
            const { data: pricing } = await supabase
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .from('materials_with_pricing' as any)
              // Sfrido e smaltimento arrivano dalla vista gia` RISOLTI per
              // l'organizzazione (COALESCE con l'override per-org): il join FK
              // su `materials` porterebbe i valori grezzi di catalogo, e su un
              // materiale globale l'override non arriverebbe mai alla riga.
              .select('id, unit_price, waste_percentage, disposal_percentage')
              .in('id', uniqueLayerMatIds);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const p of (pricing ?? []) as any[]) {
              layerPriceMap.set(p.id, Number(p.unit_price ?? 0));
              layerEffMap.set(p.id, {
                waste: p.waste_percentage != null ? Number(p.waste_percentage) : null,
                disposal: p.disposal_percentage != null ? Number(p.disposal_percentage) : null,
              });
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const applicaEffettivi = (mat: any) => {
            const id = mat?.id;
            if (!id) return;
            if (layerPriceMap.has(id)) mat.unit_price = layerPriceMap.get(id);
            const eff = layerEffMap.get(id);
            if (eff) {
              mat.waste_percentage = eff.waste;
              mat.disposal_percentage = eff.disposal;
            }
          };
          for (const dbL of dbLayers) {
            applicaEffettivi(dbL.materials);
            applicaEffettivi(dbL.screw_materials);
            applicaEffettivi(dbL.anchor_materials);
          }

          // Conta layer validi e blocca se vuoti.
          const validLayerCount = dbLayers.filter(
            (l) => !!l.materials && Number(l.thickness ?? 0) > 0,
          ).length;
          if (validLayerCount === 0) {
            skipped.push({
              id: estStrat.id,
              name: estStrat.name,
              reason: 'no_valid_layers',
              detail: `${dbLayers.length} layer senza materiale/spessore`,
            });
            continue;
          }

          // Costruisci LayerV2[] dai layer DB e usa la STESSA formula del
          // preview/save (computeStratigraphyCosts, category-aware).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sortedDbLayers = [...dbLayers].sort((a: any, b: any) => a.position - b.position);
          const layersForCompute = sortedDbLayers.map((dbL, idx) => ({
            id: dbL.id ?? `tmp-${idx}`,
            position: idx + 1,
            materialId: dbL.material_id ?? null,
            material: dbL.materials ?? null,
            thickness: Number(dbL.thickness ?? 0),
            interAxis: dbL.inter_axis ?? undefined,
            screwMaterialId: dbL.screw_material_id ?? null,
            screwMaterial: dbL.screw_materials ?? null,
            anchorMaterialId: dbL.anchor_material_id ?? null,
            anchorMaterial: dbL.anchor_materials ?? null,
            screwQuantity: dbL.screw_quantity ?? undefined,
            screwCostPerSqm: dbL.screw_cost_per_sqm ?? undefined,
            // Consumo dichiarato sul layer. Senza questa riga il motore ricade
            // sull'incidenza di catalogo e il preventivo valorizza una quantita`
            // diversa da quella che la RDA ordina: si vendono 2 kg e se ne
            // comprano 3.
            materialQuantity: dbL.material_quantity_per_sqm ?? null,
          })) as unknown as LayerV2[];

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const studSpacingMm = Number((original as any).stud_spacing_mm ?? 600);
          const breakdown = computeStratigraphyCosts(layersForCompute, studSpacingMm, costPerHour);

          const result = {
            materialCost: Math.round(breakdown.subtotalMaterials * 1000) / 1000,
            screwCost: Math.round(breakdown.subtotalScrews * 1000) / 1000,
            laborCost: Math.round(breakdown.laborCost * 1000) / 1000,
            installationTime: Math.round(breakdown.laborMinutes * 1000) / 1000,
            comprehensiveCost: Math.round(breakdown.totalCost * 100) / 100,
          };

          // Protezione: NON sovrascrivere il preventivo con 0€. Marca come da
          // revisionare invece. Capita quando la stratigrafia originale è
          // corrotta (materials cancellati, prezzi azzerati, ecc.).
          if (result.comprehensiveCost <= 0) {
            skipped.push({
              id: estStrat.id,
              name: estStrat.name,
              reason: 'zero_cost_recalc',
              detail: `${validLayerCount} layer validi ma costo totale calcolato = 0€`,
            });
            continue;
          }

          // FINITURA — RILETTURA, non solo ri-prezzatura.
          //
          // Prima questo blocco iterava sull'elenco CONGELATO nella riga e ne
          // rinfrescava solo il prezzo dei materiali gia` presenti. Le tabelle
          // `finish_levels` / `finish_level_components` non venivano mai lette:
          // un componente aggiunto in Impostazioni non entrava mai, uno rimosso
          // continuava a essere addebitato, e una quantita` o i minuti
          // modificati venivano ignorati. `finish_labor_minutes_per_sqm` non era
          // nemmeno fra i campi scritti.
          //
          // Ora si riusa `fetchFinishCostSnapshot`, la stessa funzione gia` usata
          // dall'inserimento riga e dal cambio livello a tendina: un solo posto
          // che sa come si compone il costo di una finitura.
          let newFinishCost = 0;
          let newFinishLaborMin: number | null = null;
          let refreshedFinishComponents: Array<Record<string, unknown>> | null = null;
          // Traccia se la rilettura e` davvero avvenuta: senza, `newFinishCost`
          // resterebbe 0 e quello 0 finirebbe sia in `unit_cost` sia nella
          // colonna, CANCELLANDO un costo gia` salvato invece di lasciarlo stare.
          let finishRecomputed = false;
          const { data: currentRow } = await supabase
            .from('estimate_stratigraphies')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .select('finish_level, finish_components_data, finish_labor_minutes_per_sqm, finish_cost_per_sqm' as any)
            .eq('id', estStrat.id)
            .maybeSingle();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const finishLevelCode = (currentRow as any)?.finish_level as string | null | undefined;
          if (finishLevelCode && organizationId) {
            // Le facce si ricavano dai layer come fa il create. Verificato sui
            // dati: su tutte le righe con layer congelati il valore ricalcolato
            // coincide con quello gia` salvato (33 a 2 facce, 19 a 1).
            const faces = finishedFacesFor(sortedDbLayers);
            const snap = await finishSnapshotFor(finishLevelCode, faces);
            if (snap) {
              newFinishCost = snap.finishCostPerSqm;
              newFinishLaborMin = snap.finishLaborMinPerSqm;
              refreshedFinishComponents = snap.finishComponentsSnapshot;
              finishRecomputed = true;
            }
          }
          if (!finishRecomputed) {
            // Nessun livello, org ignota o livello sparito: si conserva quello
            // che c'e` gia`, cosi` la finitura non sparisce dal costo unitario.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            newFinishLaborMin = (currentRow as any)?.finish_labor_minutes_per_sqm ?? null;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const prev = Number((currentRow as any)?.finish_cost_per_sqm ?? 0);
            newFinishCost = Number.isFinite(prev) ? prev : 0;
          }

          // `unit_cost` va arrotondato ai centesimi perche' e' un prezzo al m²
          // mostrato a video, ma il TOTALE non si calcola su quel valore
          // arrotondato: su 702 m² un errore di mezzo centesimo diventa 4,21 €,
          // e la riga del preventivo non tornava col "Costo base" della vista
          // vendita (che somma le componenti non arrotondate).
          // SFRIDO E SMALTIMENTO nel costo salvato: stessa funzione che alimenta
          // il prezzo di vendita, cosi` vale `area x unit_cost = costBase`.
          // I layer sono gli stessi su cui l'overlay ha gia` scritto i valori
          // effettivi per l'organizzazione.
          const extra = wasteAndDisposalPerSqm(sortedDbLayers, costOpts);
          const unitCostExact = breakdown.totalCost + newFinishCost + extra.waste + extra.disposal;
          const newUnitCost = Math.round(unitCostExact * 100) / 100;

          // F17 post-2026-05-22: per i ceiling con profilo perimetrale collegato,
          // ricalcola perimeter_profile_cost dal prezzo NETTO live del materiale.
          // Formula: net_price €/ml × perimeter_m × 1.05 (sfrido 5% tagli spigoli).
          let newPerimeterProfileCost: number | null = null;
          const { data: currentPerim } = await supabase
            .from('estimate_stratigraphies')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .select('perimeter_m, perimeter_profile_id' as any)
            .eq('id', estStrat.id)
            .maybeSingle();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perimM = Number((currentPerim as any)?.perimeter_m ?? 0);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perimProfId = (currentPerim as any)?.perimeter_profile_id as string | undefined;
          if (perimM > 0 && perimProfId) {
            const { data: prof } = await supabase
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .from('materials_with_pricing' as any)
              .select('unit_price, net_price')
              .eq('id', perimProfId)
              .maybeSingle();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const pricePerMl = Number((prof as any)?.net_price ?? (prof as any)?.unit_price ?? 0);
            if (pricePerMl > 0) {
              newPerimeterProfileCost = Math.round(pricePerMl * perimM * 1.05 * 100) / 100;
            }
          }

          // total_cost = area × unit_cost + extra manodopera difficolta + perimeter.
          // La difficolta` si applica SOLO alla MANODOPERA (base + finitura), non ai
          // materiali (stessa regola di useUpdateEstimateStratigraphy). Prima questo
          // bulk update PERDEVA del tutto la difficolta`: ora la preserva sul labor.
          const diff = (estStrat as { difficultyMultiplier?: number }).difficultyMultiplier ?? 1;
          const laborPerSqm = ((result.installationTime + (newFinishLaborMin ?? 0)) / 60) * costPerHour;
          const laborExtra = Math.abs(diff - 1) < 1e-9
            ? 0
            : estStrat.area * laborPerSqm * (diff - 1);
          const perimAdd = newPerimeterProfileCost ?? 0;
          // Si moltiplica il costo unitario ESATTO, non quello arrotondato a
          // video: e' la stessa base che usa la vista vendita, quindi i due
          // totali coincidono.
          const newTotalCost = Math.round((estStrat.area * unitCostExact + laborExtra + perimAdd) * 100) / 100;

          // Snapshot composition aggiornato col nuovo breakdown.
          const updatedStratigraphyData = {
            ...original,
            material_cost_per_sqm: result.materialCost,
            screw_cost_per_sqm: result.screwCost,
            labor_cost_per_sqm: result.laborCost,
            comprehensive_cost_per_sqm: result.comprehensiveCost,
            installation_time_per_sqm: result.installationTime,
            cost_per_sqm: result.comprehensiveCost,
          };

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sortedLayers = [...dbLayers].sort((a: any, b: any) => a.position - b.position);

          const updatePayload: Record<string, unknown> = {
            unit_cost: newUnitCost,
            total_cost: newTotalCost,
            stratigraphy_data: updatedStratigraphyData,
            layers_data: sortedLayers,
            // Scritti SOLO se la rilettura e` avvenuta: altrimenti i campi
            // restano come sono, invece di essere azzerati.
            // `finish_labor_minutes_per_sqm` prima non veniva mai aggiornato:
            // cambiare i minuti di un livello in Impostazioni non arrivava mai
            // ai preventivi.
            ...(finishRecomputed
              ? {
                  finish_cost_per_sqm: newFinishCost > 0 ? newFinishCost : null,
                  finish_labor_minutes_per_sqm: newFinishLaborMin,
                  finish_components_data: refreshedFinishComponents ?? [],
                }
              : {}),
            // F17 post-2026-05-22: refresh perimeter_profile_cost solo se il
            // ceiling ha un profilo collegato. Per le pareti il valore non viene
            // mai aggiornato (null fisso).
            ...(newPerimeterProfileCost !== null ? { perimeter_profile_cost: newPerimeterProfileCost } : {}),
            prices_updated_at: new Date().toISOString(),
          };
          const { error: updateError } = await supabase
            .from('estimate_stratigraphies')
            .update(updatePayload)
            .eq('id', estStrat.id);

          if (updateError) {
            failed.push({ id: estStrat.id, name: estStrat.name, error: updateError.message });
            continue;
          }

          updated.push({
            id: estStrat.id,
            name: estStrat.name,
            oldCost: estStrat.unitCost,
            newCost: newUnitCost,
            changed: Math.abs(estStrat.unitCost - newUnitCost) > 0.01,
          });
        } catch (err) {
          failed.push({
            id: estStrat.id,
            name: estStrat.name,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      return { estimateId, updated, skipped, failed, costPerHour };
    },
    onSuccess: (report, variables) => {
      queryClient.invalidateQueries({ queryKey: ['estimate-stratigraphies', report.estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimate', report.estimateId] });
      // Il badge "Prezzi disallineati" vive in cache separata (confronta
      // materials.updated_at con prices_updated_at). Senza queste invalidate
      // restava rosso anche a preventivo appena aggiornato, fino a un reload
      // della pagina: l'utente vedeva l'allarme e non sapeva cosa altro fare.
      queryClient.invalidateQueries({ queryKey: ['estimate-row-stale-status'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stale-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-estimates-comprehensive'] });
      // Toast riassuntivo conciso. Il dialog di esito viene mostrato dal
      // componente chiamante (EstimateStratigraphiesSection) usando il report.
      // I chiamanti single-row passano silentToast=true per gestirlo loro.
      if (variables.silentToast) return;
      const realChanges = report.updated.filter(r => r.changed).length;
      if (realChanges > 0) {
        toast.success(`${realChanges} stratigrafie aggiornate con prezzi modificati`);
      } else if (report.updated.length > 0) {
        toast.info(`${report.updated.length} stratigrafie verificate, prezzi già aggiornati`);
      } else if (report.skipped.length > 0 && report.failed.length === 0) {
        toast.warning('Nessuna stratigrafia aggiornata: vedi dettagli');
      }
    },
    onError: (error) => {
      console.error('[useBulkUpdateEstimateStratigraphyPrices] error:', error);
      const msg = error.message?.includes('contrattualizzato')
        ? 'Impossibile aggiornare prezzi: preventivo contrattualizzato'
        : `Errore aggiornamento prezzi: ${error.message ?? 'sconosciuto'}`;
      toast.error(msg);
    },
  });

  return {
    bulkUpdatePrices: bulkUpdateMutation.mutate,
    /** Variante async: ritorna il report strutturato (per UI di esito). */
    bulkUpdatePricesAsync: bulkUpdateMutation.mutateAsync,
    isBulkUpdating: bulkUpdateMutation.isPending,
  };
};
